import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Seo from "@/components/feature/Seo";
import EventTimeline from "./components/EventTimeline";
import DiscussionList from "./components/DiscussionList";
import RelatedEvents from "./components/RelatedEvents";
import { events, getEventById } from "@/mocks/events";

const SITE = "https://9jzs.cn";

const toc = [
  { id: "intro", label: "事件概述" },
  { id: "timeline", label: "完整时间线" },
  { id: "discussion", label: "网友讨论" },
  { id: "notes", label: "整理说明" },
];

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const event = id ? getEventById(id) : undefined;

  if (!event) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Seo
          title="档案不存在 · 瓜田观察站"
          description="你访问的事件档案不存在或已被合并整理，返回档案库继续浏览其他网络热点事件。"
          keywords="事件档案,档案不存在,网络热点"
          path="/topics"
        />
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-32">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300" aria-hidden="true"></i>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              这份档案还没整理好
            </h1>
            <p className="mt-3 font-body text-sm text-foreground-600">
              可能链接已经更新，或该事件已被合并到其他档案中。
            </p>
            <Link to="/topics" className="btn-primary mt-7">
              返回档案库
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const index = events.findIndex((item) => item.id === event.id);
  const prev = index > 0 ? events[index - 1] : undefined;
  const next = index < events.length - 1 ? events[index + 1] : undefined;

  const related = events
    .filter((item) => item.id !== event.id && item.categoryId === event.categoryId)
    .concat(events.filter((item) => item.id !== event.id && item.categoryId !== event.categoryId))
    .slice(0, 2);

  const pageTitle = `${event.title} · 事件时间线 · 瓜田观察站`;
  const pageDesc = `${event.summary} 本文按时间顺序整理该事件的公开信息，含网友讨论与相关档案推荐。`;
  const pageKeywords = `${event.tags.join(",")},事件时间线,网络热点,${event.categoryName},瓜田观察站`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: event.title,
      description: pageDesc,
      datePublished: event.date,
      dateModified: "2026-09-10",
      inLanguage: "zh-CN",
      articleSection: event.categoryName,
      keywords: event.tags.join(","),
      image: [event.cover],
      author: { "@type": "Organization", name: "瓜田观察站", url: SITE },
      publisher: { "@type": "Organization", name: "瓜田观察站", url: SITE },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/topic/${event.id}` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "事件档案库", item: `${SITE}/topics` },
        { "@type": "ListItem", position: 3, name: event.title, item: `${SITE}/topic/${event.id}` },
      ],
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Seo
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        path={`/topic/${event.id}`}
        ogType="article"
        image={event.cover}
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100 pb-10 pt-24 md:pb-14 md:pt-32">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <Breadcrumb items={[{ label: "事件档案库", to: "/topics" }, { label: event.title }]} />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/topics?category=${event.categoryId}`}
                className="rounded-md bg-primary-500 px-2.5 py-1 font-label text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {event.categoryName}
              </Link>
              <span className="rounded-full bg-background-50 px-2.5 py-1 font-label text-xs text-foreground-600">
                {event.status}
              </span>
              <span className="font-label text-xs text-foreground-500">收录于 {event.date}</span>
            </div>
            <h1 className="mt-5 max-w-4xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-5xl">
              {event.title}
            </h1>
            <p className="mt-5 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              {event.summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-label text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                讨论热度 {event.heat}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line text-secondary-500" aria-hidden="true"></i>
                {event.comments} 条讨论
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-accent-600" aria-hidden="true"></i>
                最后更新 2026-09-10
              </span>
            </div>
          </div>
        </section>

        <section className="w-full pb-16 pt-10 md:pt-14">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-[1fr_300px] lg:gap-12">
            <article>
              <div className="w-full overflow-hidden rounded-lg border border-background-200">
                <img
                  src={event.cover}
                  alt={`${event.title} 事件配图`}
                  title={`${event.title} · 事件档案配图`}
                  className="h-56 w-full object-cover object-top md:h-80"
                />
              </div>

              <section id="intro" className="scroll-mt-28 pt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件概述</h2>
                <p className="mt-4 font-body text-sm leading-7 text-foreground-700 md:text-base">{event.intro}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {event.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </section>

              <section id="timeline" className="scroll-mt-28 pt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">完整时间线</h2>
                <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
                  以下节点均来自公开渠道，按发生时间先后排列。若后续出现新的公开进展，我们会在此处继续追加。
                </p>
                <EventTimeline nodes={event.timeline} />
              </section>

              <section id="discussion" className="scroll-mt-28 pt-10">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">网友讨论</h2>
                  <span className="font-label text-xs text-foreground-500">精选高赞观点，不代表本站立场</span>
                </div>
                <DiscussionList items={event.discussions} />
              </section>

              <section id="notes" className="scroll-mt-28 pt-10">
                <div className="rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-accent-950">
                    <i className="ri-information-line text-accent-700" aria-hidden="true"></i>
                    整理说明
                  </h2>
                  <ul className="mt-3 flex flex-col gap-2">
                    {event.notes.map((note) => (
                      <li key={note} className="flex gap-2 font-body text-sm leading-relaxed text-accent-900">
                        <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-600" aria-hidden="true"></i>
                        {note}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 border-t border-accent-200 pt-4 font-body text-xs leading-relaxed text-accent-800">
                    如发现内容有误，欢迎通过「关于我们」页面的联系表单提交纠正信息，并附上公开来源链接。
                  </p>
                </div>
              </section>

              <nav className="mt-10 flex flex-col gap-4 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                {prev ? (
                  <Link to={`/topic/${prev.id}`} className="group flex-1">
                    <span className="font-label text-xs text-foreground-500">上一篇档案</span>
                    <p className="mt-1 line-clamp-1 font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                      ← {prev.title}
                    </p>
                  </Link>
                ) : (
                  <span className="flex-1 font-label text-xs text-foreground-400">已经是第一篇档案</span>
                )}
                {next ? (
                  <Link to={`/topic/${next.id}`} className="group flex-1 sm:text-right">
                    <span className="font-label text-xs text-foreground-500">下一篇档案</span>
                    <p className="mt-1 line-clamp-1 font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {next.title} →
                    </p>
                  </Link>
                ) : (
                  <span className="flex-1 font-label text-xs text-foreground-400 sm:text-right">已经是最后一篇</span>
                )}
              </nav>

              <RelatedEvents items={related} />
            </article>

            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-label text-xs font-medium tracking-wide text-foreground-500">本页目录</h2>
                <nav aria-label="页内目录" className="mt-3 flex flex-col">
                  {toc.map((item, i) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="flex items-center gap-2 border-b border-background-200 py-2.5 font-body text-sm text-foreground-700 transition-colors last:border-0 hover:text-primary-600"
                    >
                      <span className="font-label text-xs text-foreground-400">0{i + 1}</span>
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>

              <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-primary-950">
                  <i className="ri-shield-star-line text-primary-600" aria-hidden="true"></i>
                  围观提示
                </h2>
                <p className="mt-2 font-body text-sm leading-relaxed text-primary-900/80">
                  讨论请围绕事件本身，不要扩散任何个人身份信息。看到可疑内容，欢迎提交给我们核对。
                </p>
                <Link to="/about#contact" className="btn-primary mt-4 w-full">
                  提交线索
                </Link>
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-label text-xs font-medium tracking-wide text-foreground-500">同类档案</h2>
                <ul className="mt-3 flex flex-col gap-3">
                  {events
                    .filter((item) => item.categoryId === event.categoryId && item.id !== event.id)
                    .slice(0, 3)
                    .map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/topic/${item.id}`}
                          className="line-clamp-2 font-body text-sm leading-snug text-foreground-700 transition-colors hover:text-primary-600"
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}